// Stage 1 — Find a Preprint (FR-1.*, July-7 FR-K1–K5). Discovery defaults to the Unclaimed
// (Unread) pool across ALL domains for every role; July-8 Bucket 2 adds richer cards, multi-domain
// filter, and human-facing Unclaimed/Claimed labels while API values stay verbatim.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DOMAINS,
  SELECTED_STATUSES,
  ASSIGNMENT_CAP,
  DOMAIN_LABELS,
  selectedStatusLabel,
  type Domain,
  type SelectedStatus,
  type SelectedView,
} from "@rrid/shared-types";
import { api, ApiError } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { useCan } from "../../components/Can.js";
import { Badge, Button, Card, CardBody, EmptyState, Select, Spinner, statusTone } from "../../components/ui.js";
import { confirmAdvance } from "./advance-confirms.js";

const NEXT_STATUS: Partial<Record<SelectedStatus, { status: SelectedStatus; label: string }>> = {
  Taken: { status: "To Pitch(Team)", label: "Ready to pitch (Team)" },
  "To Pitch(Team)": { status: "To Pitch(Editorial)", label: "Advance to Editorial" },
  "To Pitch(Editorial)": { status: "Selected", label: "Set Selected" },
};

const REFINE_STATUSES: SelectedStatus[] = ["To Pitch(Team)", "To Pitch(Editorial)"];

interface AssignmentSlots {
  used: number;
  cap: number;
  remaining: number;
}

function toggleDomain(current: Domain[], d: Domain): Domain[] {
  return current.includes(d) ? current.filter((x) => x !== d) : [...current, d];
}

export function PreprintsPortal(): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [domains, setDomains] = useState<Domain[]>([]);
  const COMBINED = "editorial+selected";
  const ALL = "all";
  const [status, setStatus] = useState<SelectedStatus | typeof COMBINED | typeof ALL>("Unread");
  const [mine, setMine] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  const params = new URLSearchParams();
  if (domains.length) params.set("domains", domains.join(","));
  if (status === COMBINED) params.set("statuses", "To Pitch(Editorial),Selected");
  else params.set("status", status);
  if (mine) params.set("mine", "1");
  const key = Object.fromEntries(params);

  const { data, isLoading } = useQuery({
    queryKey: qk.selected(key),
    queryFn: () => api.get<SelectedView[]>(`/preprints/selected?${params.toString()}`),
  });
  const { data: slots } = useQuery({
    queryKey: ["assignment-slots"],
    queryFn: () => api.get<AssignmentSlots>(`/preprints/selected/my-slots`),
  });

  const assign = useMutation({
    mutationFn: (id: string) => api.post<SelectedView>(`/preprints/selected/${id}/assign`),
    onSuccess: (updated) => {
      setAssignError(null);
      qc.invalidateQueries({ queryKey: ["selected"] });
      qc.invalidateQueries({ queryKey: ["assignment-slots"] });
      navigate(`/preprints/${updated.id}`);
    },
    onError: (err) => {
      const body = err instanceof ApiError && err.body && typeof err.body === "object" ? (err.body as { error?: unknown }) : null;
      setAssignError(typeof body?.error === "string" ? body.error : "Could not assign this preprint.");
      qc.invalidateQueries({ queryKey: ["assignment-slots"] });
    },
  });
  const advance = useMutation({
    mutationFn: ({ id, status }: { id: string; status: SelectedStatus }) =>
      api.post(`/preprints/selected/${id}/advance`, { status, aeChecks: { confirmed: true } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["selected"] });
      qc.invalidateQueries({ queryKey: ["assignment-slots"] });
    },
  });
  const setUrgent = useMutation({
    mutationFn: ({ id, urgent }: { id: string; urgent: boolean }) => api.post(`/preprints/selected/${id}/urgent`, { urgent }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["selected"] }),
  });
  const refine = useMutation({
    mutationFn: (id: string) => api.post(`/preprints/selected/${id}/refine`, { mode: "reviewers" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["selected"] }),
  });
  const rankInput = useMutation({
    mutationFn: ({ id, rank }: { id: string; rank: number }) => api.post(`/preprints/selected/${id}/rank-input`, { rank }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["selected"] }),
  });

  const noSlotsLeft = slots != null && slots.remaining <= 0;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Find a Preprint</h1>
          <p className="text-sm text-slate-500">
            Hunt new (Unclaimed) preprints in any domain, assign one to yourself, and pitch it from its Details page.
          </p>
          {slots && (
            <p className="mt-1 text-xs text-slate-500">
              Assignment slots: <span className={noSlotsLeft ? "font-semibold text-red-600" : "font-semibold"}>{slots.used}/{slots.cap}</span> used
              {noSlotsLeft && " — submit a pitch to free a slot"}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <details className="group relative">
            <summary className="cursor-pointer list-none rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              Domains: {domains.length ? domains.join(", ") : "All"}
            </summary>
            <div className="absolute right-0 z-10 mt-1 min-w-[12rem] rounded-md border border-slate-200 bg-white p-2 shadow-lg">
              <p className="mb-1 text-xs font-medium text-slate-500">Select one or more</p>
              {DOMAINS.map((d) => (
                <label key={d} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={domains.includes(d)}
                    onChange={() => setDomains((cur) => toggleDomain(cur, d))}
                  />
                  <span>
                    {d} <span className="text-xs text-slate-400">({DOMAIN_LABELS[d]})</span>
                  </span>
                </label>
              ))}
              {domains.length > 0 && (
                <button type="button" className="mt-1 text-xs text-brand hover:underline" onClick={() => setDomains([])}>
                  Clear filter
                </button>
              )}
            </div>
          </details>
          <Select value={status} onChange={(e) => setStatus(e.target.value as SelectedStatus | typeof COMBINED | typeof ALL)}>
            <option value={ALL}>All statuses</option>
            <option value={COMBINED}>Editorial + Selected</option>
            {SELECTED_STATUSES.map((s) => (
              <option key={s} value={s}>
                {selectedStatusLabel(s)}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-1.5 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={mine}
              onChange={(e) => {
                setMine(e.target.checked);
                if (e.target.checked && status === "Unread") setStatus(ALL);
              }}
            />{" "}
            My Preprints
          </label>
        </div>
      </header>

      {assignError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700" role="alert">
          {assignError}
        </div>
      )}

      {isLoading ? (
        <Spinner label="Loading preprints…" />
      ) : (data ?? []).length === 0 ? (
        <EmptyState>No preprints match these filters.</EmptyState>
      ) : (
        <div className="space-y-3">
          {(data ?? []).map((s) => {
            const next = NEXT_STATUS[s.status];
            const ctx = { domain: s.domain, beforePitching: s.status === "Taken" };
            const own = !!user && !!s.assigneeEmail && s.assigneeEmail.toLowerCase() === user.email.toLowerCase();
            const advanceAction =
              s.status === "Taken"
                ? "setToPitchTeam"
                : s.status === "To Pitch(Team)"
                  ? "advanceToPitchEditorial"
                  : "setToSelected";
            const canAdvance =
              !!next && (advanceAction === "setToPitchTeam" ? own || can(advanceAction, ctx) : can(advanceAction, {}));
            const rejectTarget: SelectedStatus | null =
              s.status === "To Pitch(Team)"
                ? "Team Rejected"
                : s.status === "To Pitch(Editorial)"
                  ? "Editorial Rejected"
                  : null;
            const canReject = !!rejectTarget && can(advanceAction, {});
            const isUrgent = (s.pitch as Record<string, unknown>).Urgent === "Urgent";
            const titleEl = s.doi ? (
              <a className="text-brand hover:underline" href={s.doi} target="_blank" rel="noreferrer">
                {s.title} ↗
              </a>
            ) : (
              s.title
            );
            return (
              <Card key={s.id}>
                <CardBody className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={statusTone(s.status)}>{selectedStatusLabel(s.status)}</Badge>
                      {s.domain && <Badge tone="blue">{s.domain}</Badge>}
                      {s.gatesFunded && <Badge tone="amber">Gates</Badge>}
                      {isUrgent && <Badge tone="red">Urgent</Badge>}
                      {s.proposalStatus && <Badge tone="blue">{s.proposalStatus}</Badge>}
                      {s.avgRanking != null && <Badge tone="blue">avg rank {s.avgRanking}</Badge>}
                    </div>
                    <h3 className="mt-1 font-medium leading-snug">{titleEl}</h3>
                    <p className="text-xs text-slate-500">
                      {s.publishedDate ? `Published ${s.publishedDate}` : "Publication date unknown"}
                    </p>
                    {s.abstractBlurb && (
                      <p className="mt-2 line-clamp-3 rounded-md border border-slate-100 bg-slate-50 px-2 py-1.5 text-xs text-slate-600">
                        {s.abstractBlurb}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-slate-500">
                      {s.assigneeEmail ? (
                        <>
                          {s.assigneeName ? `${s.assigneeName} · ` : ""}
                          <a className="text-brand hover:underline" href={`mailto:${s.assigneeEmail}`}>
                            {s.assigneeEmail}
                          </a>
                        </>
                      ) : (
                        "Unassigned"
                      )}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {s.status === "Unread" && can("assignPreprint") && (
                      <Button
                        onClick={() => assign.mutate(s.id)}
                        disabled={assign.isPending || noSlotsLeft}
                        title={noSlotsLeft ? `You already hold ${ASSIGNMENT_CAP} preprints awaiting pitch — submit one to Pitch Team first.` : undefined}
                      >
                        {assign.isPending ? "Assigning…" : noSlotsLeft ? "No slots left" : "Assign to me"}
                      </Button>
                    )}
                    {s.status !== "Unread" && (
                      <Link to={`/preprints/${s.id}`}>
                        <Button variant="secondary">Details</Button>
                      </Link>
                    )}
                    {canAdvance && next && (
                      <Button
                        onClick={() => {
                          if (!confirmAdvance(next.status)) return;
                          advance.mutate({ id: s.id, status: next.status });
                        }}
                        disabled={advance.isPending}
                      >
                        {next.label}
                      </Button>
                    )}
                    {canReject && rejectTarget && (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          if (window.confirm(`Reject this preprint at the ${selectedStatusLabel(s.status)} gate?`)) {
                            advance.mutate({ id: s.id, status: rejectTarget });
                          }
                        }}
                        disabled={advance.isPending}
                      >
                        Reject
                      </Button>
                    )}
                    {s.status !== "Unread" && (own || can("editPitchDeck", ctx)) && (
                      <Button variant="ghost" onClick={() => setUrgent.mutate({ id: s.id, urgent: !isUrgent })} disabled={setUrgent.isPending}>
                        {isUrgent ? "Unset urgent" : "Set urgent"}
                      </Button>
                    )}
                    {REFINE_STATUSES.includes(s.status) && can("editSimilarArticles", ctx) && (
                      <Button variant="ghost" onClick={() => refine.mutate(s.id)} disabled={refine.isPending}>
                        Refine reviewers
                      </Button>
                    )}
                    {can("inputRanking", ctx) && (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          const cur = user ? s.rankings[user.email.toLowerCase()] : undefined;
                          const v = window.prompt("Your ranking (1 = highest priority):", cur != null ? String(cur) : "");
                          const n = v == null ? NaN : Number.parseInt(v, 10);
                          if (Number.isInteger(n) && n > 0) rankInput.mutate({ id: s.id, rank: n });
                        }}
                        disabled={rankInput.isPending}
                      >
                        Input ranking
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
